import classNamesHelper from "../../helpers/classNameHelper";
import React, { createElement, JSX, ReactElement } from "react";
import styles from "./Typography.module.scss";

interface ITypographyProps {
  component?: "h1" | "h2" | "h3" | "h4" | "h5" | "span" | "p" | "label";
  className?: string;
  children: React.ReactNode;
  weight?: TypographyWeight;
  onClick?: () => void;
  role?: string;
  as?: keyof JSX.IntrinsicElements;
  htmlFor?: string;
}

export type TypographySize = "xs" | "sm" | "md" | "lg" | "xl";
export type TypographyWeight = "regular" | "medium" | "semibold" | "bold";

type HeadlineTypography = {
  variant?: "headline";
  type: 1 | 2 | 3 | 4 | 5;
  size?: never;
};

type DataTypography = {
  variant?: "data";
  size?: TypographySize;
  type?: never;
};

type TextTypography = {
  variant?: "text";
  size?: TypographySize;
  type?: never;
};

type ButtonTypography = {
  variant?: "button";
  size?: TypographySize;
  type?: never;
};

type InputTypography = {
  variant?: "input";
  size?: "content";
  type?: never;
};

type LabelTypography = {
  variant?: "label";
  size?: TypographySize;
  type?: never;
};

type TypographyConditionalProps =
  | HeadlineTypography
  | DataTypography
  | TextTypography
  | ButtonTypography
  | InputTypography
  | LabelTypography;

/**
 * @param component - Type of inline component.
 * @param className - Additional styling
 * @param children  - Element to be rendered.
 * @param variant   - Typography type.
 * @param type      - Headline size.
 * @param size      - Font size.
 * @param weight    - Font weight.
 * @param onClick   - Callback function for the onClick event
 * @returns Typography component
 */

const Typography = ({
  component,
  className,
  children,
  variant = "text",
  type,
  size = "sm",
  weight = "regular",
  onClick = () => {},
  role = "",
  ...rest // <-- add this
}: ITypographyProps & TypographyConditionalProps): ReactElement => {
  if (!component) {
    if (variant === "headline" && type) {
      component = `h${type}`;
    } else component = "span";
  }
  const computedClassName =
    variant === "headline"
      ? `${variant}-${type}-${weight}`
      : `${variant}-${size}-${weight}`;
  return createElement(
    component,
    {
      className: classNamesHelper([styles[computedClassName], className]),
      onClick: onClick,
      role: role,
      ...rest,
    },
    children
  );
};

export default Typography;
